import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Clock, Download, CheckCircle, AlertCircle, Users, FileDown, Filter, MapPin, Home, Calendar, PartyPopper, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authFetch, API } from '../utils/api';
import { toast } from 'sonner';
import GroupBadge from '../components/GroupBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Reports({ user }) {
  const [history, setHistory] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [attendanceDashboard, setAttendanceDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    // Initialize with current month dates
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      start: startDate.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  });
  const [preset, setPreset] = useState('month');
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [dailyHours, setDailyHours] = useState([]);
  const [dailyHoursLoading, setDailyHoursLoading] = useState(false);
  const [dailyBreakdown, setDailyBreakdown] = useState(null);
  const [dailyBreakdownLoading, setDailyBreakdownLoading] = useState(false);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('all');

  useEffect(() => {
    // Fetch data with initial date range
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const start = startDate.toISOString().split('T')[0];
    const end = today.toISOString().split('T')[0];
    fetchReportsData(start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);

    if (user.role === 'admin') {
      fetchGroups();
      fetchEmployees();
      fetchDailyHours(start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);
      fetchDailyBreakdown(selectedDayOfWeek, start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);
    }
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await authFetch(`${API}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/team/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDailyHours = async (startDate = null, endDate = null, groupId = null) => {
    if (user.role !== 'admin') return;

    setDailyHoursLoading(true);
    try {
      let url = `${API}/reports/daily-hours`;
      const params = [];

      if (startDate && endDate) {
        params.push(`start_date=${startDate}&end_date=${endDate}`);
      }
      if (groupId && groupId !== 'all') {
        params.push(`group_id=${groupId}`);
      }

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setDailyHours(data);
      }
    } catch (error) {
      console.error('Error fetching daily hours:', error);
    } finally {
      setDailyHoursLoading(false);
    }
  };

  const fetchDailyBreakdown = async (dayOfWeek = null, startDate = null, endDate = null, groupId = null) => {
    if (user.role !== 'admin') return;

    setDailyBreakdownLoading(true);
    try {
      let url = `${API}/reports/daily-breakdown`;
      const params = [];

      if (dayOfWeek && dayOfWeek !== 'all') {
        params.push(`day_of_week=${dayOfWeek}`);
      }
      if (startDate && endDate) {
        params.push(`start_date=${startDate}&end_date=${endDate}`);
      }
      if (groupId && groupId !== 'all') {
        params.push(`group_id=${groupId}`);
      }

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setDailyBreakdown(data);
      }
    } catch (error) {
      console.error('Error fetching daily breakdown:', error);
    } finally {
      setDailyBreakdownLoading(false);
    }
  };

  const fetchReportsData = async (startDate = null, endDate = null, groupId = null) => {
    try {
      let historyUrl = `${API}/time/history?limit=1000`;
      let leavesUrl = user.role === 'admin' ? `${API}/leave/all` : `${API}/leave/my-requests`;
      let myReportUrl = `${API}/reports/my-report`;

      if (startDate && endDate) {
        const params = `start_date=${startDate}&end_date=${endDate}`;
        historyUrl += `&${params}`;
        myReportUrl += `?${params}`;
      }

      const requests = [
        authFetch(historyUrl),
        authFetch(leavesUrl),
        authFetch(myReportUrl)
      ];

      // Add attendance dashboard request for admins
      if (user.role === 'admin') {
        let dashboardUrl = `${API}/reports/attendance-dashboard`;
        const params = [];
        if (startDate && endDate) {
          params.push(`start_date=${startDate}&end_date=${endDate}`);
        }
        if (groupId && groupId !== 'all') {
          params.push(`group_id=${groupId}`);
        }
        if (params.length > 0) {
          dashboardUrl += `?${params.join('&')}`;
        }
        requests.push(authFetch(dashboardUrl));
      }

      const responses = await Promise.all(requests);
      const [historyRes, leavesRes, myReportRes, dashboardRes] = responses;

      const historyData = await historyRes.json();
      const leavesData = await leavesRes.json();
      const myReportData = await myReportRes.json();

      setHistory(historyData);
      setLeaves(leavesData);
      setEmployeeReport(myReportData);

      if (dashboardRes) {
        const dashboardData = await dashboardRes.json();
        setAttendanceDashboard(dashboardData);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (presetType) => {
    setPreset(presetType);
    const today = new Date();
    let startDate = new Date();

    switch (presetType) {
      case '7days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'week':
        startDate.setDate(today.getDate() - today.getDay());
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    const start = startDate.toISOString().split('T')[0];
    const end = today.toISOString().split('T')[0];
    setDateRange({ start, end });
    fetchReportsData(start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);
    if (user.role === 'admin') {
      fetchDailyHours(start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);
      fetchDailyBreakdown(selectedDayOfWeek, start + 'T00:00:00Z', end + 'T23:59:59Z', selectedGroup);
    }
  };

  const applyCustomRange = () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please select both start and end dates');
      return;
    }
    setPreset('custom');
    fetchReportsData(dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', selectedGroup);
    if (user.role === 'admin') {
      fetchDailyBreakdown(selectedDayOfWeek, dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', selectedGroup);
    }
    if (user.role === 'admin') {
      fetchDailyHours(dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', selectedGroup);
    }
  };

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    if (dateRange.start && dateRange.end) {
      fetchReportsData(dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', groupId);
      if (user.role === 'admin') {
        fetchDailyHours(dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', groupId);
        fetchDailyBreakdown(selectedDayOfWeek, dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', groupId);
      }
    }
  };

  const handleDayOfWeekChange = (dayOfWeek) => {
    setSelectedDayOfWeek(dayOfWeek);
    if (dateRange.start && dateRange.end) {
      fetchDailyBreakdown(dayOfWeek, dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z', selectedGroup);
    }
  };

  const handleExport = async (format = 'excel', type = 'personal') => {
    try {
      let exportUrl = '';

      if (user.role === 'admin' && type === 'attendance') {
        exportUrl = `${BACKEND_URL}/api/reports/attendance-dashboard/export?format=${format}`;
      } else if (user.role === 'admin' && type === 'leave') {
        exportUrl = `${BACKEND_URL}/api/reports/leave-summary/export?format=${format}`;
      } else if (user.role === 'admin' && type === 'daily-breakdown') {
        exportUrl = `${BACKEND_URL}/api/reports/daily-breakdown/export?format=${format}`;
      } else {
        exportUrl = `${BACKEND_URL}/api/reports/my-report/export?format=${format}`;
      }

      if (dateRange.start && dateRange.end) {
        exportUrl += `&start_date=${dateRange.start}T00:00:00Z&end_date=${dateRange.end}T23:59:59Z`;
      }

      if (user.role === 'admin' && type === 'attendance' && selectedGroup && selectedGroup !== 'all') {
        exportUrl += `&group_id=${selectedGroup}`;
      }

      if (user.role === 'admin' && type === 'daily-breakdown') {
        if (selectedDayOfWeek && selectedDayOfWeek !== 'all') {
          exportUrl += `&day_of_week=${selectedDayOfWeek}`;
        }
        if (selectedGroup && selectedGroup !== 'all') {
          exportUrl += `&group_id=${selectedGroup}`;
        }
      }

      const response = await authFetch(exportUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = format === 'excel' ? 'xlsx' : 'pdf';
        const filename = type === 'attendance' ? 'attendance_dashboard' :
                        type === 'leave' ? 'leave_summary' :
                        type === 'daily-breakdown' ? 'daily_breakdown' :
                        'my_report';
        a.download = `${filename}_${new Date().toISOString().split('T')[0]}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully!');
      } else {
        toast.error('Failed to export report');
      }
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleExportEmployee = async (userId, format = 'excel') => {
    try {
      let exportUrl = `${BACKEND_URL}/api/reports/employee/${userId}/export?format=${format}`;
      if (dateRange.start && dateRange.end) {
        exportUrl += `&start_date=${dateRange.start}T00:00:00Z&end_date=${dateRange.end}T23:59:59Z`;
      }

      const response = await authFetch(exportUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = format === 'excel' ? 'xlsx' : 'pdf';
        a.download = `employee_report_${userId}_${new Date().toISOString().split('T')[0]}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Employee report exported successfully!');
      } else {
        toast.error('Failed to export employee report');
      }
    } catch (error) {
      toast.error('Failed to export employee report');
    }
  };

  const getChartData = () => {
    const dateGroups = {};

    history.forEach((entry) => {
      if (entry.duration && entry.status === 'completed') {
        const date = new Date(entry.clock_in);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!dateGroups[dateStr]) {
          dateGroups[dateStr] = 0;
        }
        dateGroups[dateStr] += entry.duration / 60;
      }
    });

    return Object.keys(dateGroups)
      .sort((a, b) => new Date(a) - new Date(b))
      .map((date) => ({
        date,
        hours: parseFloat(dateGroups[date].toFixed(2))
      }));
  };

  const getLeaveStats = () => {
    const filteredLeaves = leaves.filter(leave => {
      if (!dateRange.start || !dateRange.end) return true;
      return leave.start_date >= dateRange.start && leave.end_date <= dateRange.end;
    });

    const approved = filteredLeaves.filter(l => l.status === 'approved').length;
    const pending = filteredLeaves.filter(l => l.status === 'pending').length;
    const rejected = filteredLeaves.filter(l => l.status === 'rejected').length;

    return [
      { name: 'Approved', value: approved, color: '#10b981' },
      { name: 'Pending', value: pending, color: '#f59e0b' },
      { name: 'Rejected', value: rejected, color: '#ef4444' }
    ];
  };

  const calculateStats = () => {
    const completedEntries = history.filter(e => e.status === 'completed');
    const totalMinutes = completedEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
    const totalHours = totalMinutes / 60;
    const avgHoursPerDay = completedEntries.length > 0 ? totalHours / completedEntries.length : 0;

    return {
      totalHours: totalHours.toFixed(2),
      totalDays: completedEntries.length,
      avgHoursPerDay: avgHoursPerDay.toFixed(2)
    };
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  const chartData = getChartData();
  const leaveStats = getLeaveStats();
  const stats = calculateStats();

  const getDateRangeText = () => {
    try {
      if (!dateRange.start || !dateRange.end) {
        return 'All time';
      }
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);

      // Check if dates are valid
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 'Invalid date range';
      }

      const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} - ${end}`;
    } catch (error) {
      console.error('Error formatting date range:', error);
      return 'Invalid date range';
    }
  };

  const getSelectedGroupName = () => {
    if (!selectedGroup || selectedGroup === 'all') return 'All Groups';
    const group = groups.find(g => g.group_id === selectedGroup);
    return group ? group.name : 'Unknown Group';
  };

  const getTotalDuration = () => {
    if (user.role === 'admin' && attendanceDashboard) {
      const totalHours = attendanceDashboard.employee_stats?.reduce((sum, emp) => sum + (emp.total_hours || 0), 0) || 0;
      const totalEntries = attendanceDashboard.employee_stats?.reduce((sum, emp) => sum + (emp.total_entries || 0), 0) || 0;
      return { hours: totalHours.toFixed(2), entries: totalEntries };
    } else if (employeeReport) {
      return {
        hours: employeeReport.stats?.total_hours || 0,
        entries: employeeReport.stats?.total_entries || 0
      };
    }
    return { hours: 0, entries: 0 };
  };

  return (
    <Layout user={user}>
      <div data-testid="reports-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">
              {user.role === 'admin' ? 'Team Reports & Analytics' : 'Reports & Analytics'}
            </h1>
            <p className="text-foreground-muted mt-1">
              {user.role === 'admin' ? 'Track team performance and attendance' : 'View your work history and stats'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {user.role === 'admin' ? (
              <>
                <Button
                  onClick={() => handleExport('excel', 'attendance')}
                  className="bg-accent hover:bg-accent/90 rounded-full"
                  data-testid="export-attendance-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Attendance Excel
                </Button>
                <Button
                  onClick={() => handleExport('pdf', 'attendance')}
                  className="bg-primary hover:bg-primary-hover rounded-full"
                  data-testid="export-attendance-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Attendance PDF
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleExport('excel')}
                  className="bg-accent hover:bg-accent/90 rounded-full"
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button
                  onClick={() => handleExport('pdf')}
                  className="bg-primary hover:bg-primary-hover rounded-full"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6 mb-6" data-testid="date-range-filter">
          <h2 className="text-lg font-heading font-bold text-foreground-DEFAULT mb-4">Filters</h2>

          {/* Group Filter (Admin only) */}
          {user.role === 'admin' && groups.length > 0 && (
            <div className="mb-4 pb-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-foreground-muted" />
                <Label htmlFor="group-filter" className="text-sm font-medium text-foreground-DEFAULT">Filter by Group:</Label>
                <Select value={selectedGroup} onValueChange={handleGroupChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All groups" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All groups</SelectItem>
                    {groups.map(group => (
                      <SelectItem key={group.group_id} value={group.group_id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedGroup !== 'all' && (
                  <GroupBadge group={groups.find(g => g.group_id === selectedGroup)} />
                )}
              </div>
            </div>
          )}

          <div className="mb-2">
            <Label className="text-sm font-medium text-foreground-DEFAULT">Date Range Presets</Label>
          </div>
          <div  className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => applyPreset('7days')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === '7days'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-7days"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => applyPreset('week')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'week'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-week"
            >
              This Week
            </button>
            <button
              onClick={() => applyPreset('month')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'month'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-month"
            >
              This Month
            </button>
            <button
              onClick={() => applyPreset('year')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'year'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-year"
            >
              This Year
            </button>
          </div>

          <div  className="flex flex-wrap items-end gap-3">
            <div  className="flex-1 min-w-[150px]">
              <label  className="block text-sm font-medium text-foreground-DEFAULT mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                 className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                data-testid="start-date"
              />
            </div>
            <div  className="flex-1 min-w-[150px]">
              <label  className="block text-sm font-medium text-foreground-DEFAULT mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                 className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                data-testid="end-date"
              />
            </div>
            <Button
              onClick={applyCustomRange}
               className="bg-primary hover:bg-primary-hover rounded-full"
              data-testid="apply-custom-range"
            >
              Apply Range
            </Button>
          </div>
        </div>

        {/* Report Summary Section */}
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-slate-100 shadow-sm rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-heading font-semibold text-foreground-DEFAULT mb-4">Report Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-foreground-muted mb-1">Report For</p>
              <p className="text-base font-semibold text-foreground-DEFAULT">
                {user.role === 'admin' ? 'All Employees' : user.name}
              </p>
              {user.role !== 'admin' && user.email && (
                <p className="text-xs text-foreground-muted mt-1">{user.email}</p>
              )}
            </div>

            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-foreground-muted mb-1">Date Range</p>
              <p className="text-base font-semibold text-foreground-DEFAULT">
                {getDateRangeText()}
              </p>
              <p className="text-xs text-foreground-muted mt-1">
                {preset === '7days' && 'Last 7 Days'}
                {preset === 'week' && 'This Week'}
                {preset === 'month' && 'This Month'}
                {preset === 'year' && 'This Year'}
                {preset === 'custom' && 'Custom Range'}
              </p>
            </div>

            {user.role === 'admin' && groups.length > 0 && (
              <div className="bg-white rounded-xl p-4">
                <p className="text-sm text-foreground-muted mb-1">Group Filter</p>
                <p className="text-base font-semibold text-foreground-DEFAULT">
                  {getSelectedGroupName()}
                </p>
                {selectedGroup !== 'all' && groups.find(g => g.group_id === selectedGroup) && (
                  <div className="mt-1">
                    <GroupBadge group={groups.find(g => g.group_id === selectedGroup)} />
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-foreground-muted mb-1">Total Duration</p>
              <p className="text-2xl font-bold text-foreground-DEFAULT">
                {getTotalDuration().hours}h
              </p>
              <p className="text-xs text-foreground-muted mt-1">
                {getTotalDuration().entries} {getTotalDuration().entries === 1 ? 'entry' : 'entries'}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {user.role === 'admin' && attendanceDashboard && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-primary/10 rounded-xl p-3">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground-DEFAULT">Total Employees</h3>
                  </div>
                  <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {attendanceDashboard.summary?.total_employees || 0}
                  </p>
                  <p className="text-sm text-foreground-muted mt-2">
                    Active in system
                  </p>
                </div>

                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-accent/10 rounded-xl p-3">
                      <Clock className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="font-semibold text-foreground-DEFAULT">Active Now</h3>
                  </div>
                  <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {attendanceDashboard.summary?.active_now || 0}
                  </p>
                  <p className="text-sm text-foreground-muted mt-2">
                    Currently working
                  </p>
                </div>

                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-green-100 rounded-xl p-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-foreground-DEFAULT">Total Entries</h3>
                  </div>
                  <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {attendanceDashboard.summary?.total_time_entries || 0}
                  </p>
                  <p className="text-sm text-foreground-muted mt-2">
                    In selected period
                  </p>
                </div>

                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-orange-100 rounded-xl p-3">
                      <AlertCircle className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-foreground-DEFAULT">Leave Requests</h3>
                  </div>
                  <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {attendanceDashboard.summary?.total_leave_requests || 0}
                  </p>
                  <p className="text-sm text-foreground-muted mt-2">
                    Total requests
                  </p>
                </div>
              </div>

              <Tabs defaultValue="performance" className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                <TabsList className="mb-6">
                  <TabsTrigger value="performance">Employee Performance</TabsTrigger>
                  <TabsTrigger value="daily">Daily Hours Report</TabsTrigger>
                  <TabsTrigger value="breakdown">Daily Breakdown</TabsTrigger>
                  <TabsTrigger value="logs">Time Entry Logs</TabsTrigger>
                </TabsList>

                <TabsContent value="performance">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT">Employee Performance Summary</h2>
                    <Button
                      onClick={() => handleExport('excel', 'leave')}
                      className="bg-slate-100 hover:bg-slate-200 text-foreground-DEFAULT rounded-full"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Leave Summary
                    </Button>
                  </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Employee</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Group</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Role</th>
                        <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Total Hours</th>
                        <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Entries</th>
                        <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Leaves</th>
                        <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceDashboard.employee_stats?.map((emp) => {
                        const employee = employees.find(e => e.user_id === emp.user_id);
                        return (
                          <tr key={emp.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-foreground-DEFAULT">{emp.name}</p>
                                <p className="text-sm text-foreground-muted">{emp.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {employee?.group ? (
                                <GroupBadge group={employee.group} />
                              ) : (
                                <span className="text-xs text-foreground-muted">No group</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                emp.role === 'admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {emp.role}
                              </span>
                            </td>
                          <td className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">
                            {emp.total_hours}h
                          </td>
                          <td className="text-right py-3 px-4 text-foreground-muted">
                            {emp.total_entries}
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className="text-foreground-DEFAULT">
                              {emp.total_leaves}
                              {emp.pending_leaves > 0 && (
                                <span className="text-orange-600 ml-1">
                                  ({emp.pending_leaves} pending)
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <div className="flex gap-1 justify-end">
                              <button
                                onClick={() => handleExportEmployee(emp.user_id, 'excel')}
                                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                                title="Export to Excel"
                              >
                                <FileDown className="w-4 h-4 text-accent" />
                              </button>
                              <button
                                onClick={() => handleExportEmployee(emp.user_id, 'pdf')}
                                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                                title="Export to PDF"
                              >
                                <FileDown className="w-4 h-4 text-primary" />
                              </button>
                            </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </TabsContent>

                <TabsContent value="daily">
                  <div className="mb-6">
                    <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-2">Daily Hours Report</h2>
                    <p className="text-sm text-foreground-muted">View total hours worked per day with location breakdown</p>
                  </div>

                  {dailyHoursLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <p className="text-foreground-muted">Loading...</p>
                    </div>
                  ) : dailyHours.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                      <p className="text-foreground-muted">No data available for selected period</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Date</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Employee</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Group</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Total Hours</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">
                              <div className="flex items-center justify-end gap-1">
                                <MapPin className="w-4 h-4" />
                                Onsite
                              </div>
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">
                              <div className="flex items-center justify-end gap-1">
                                <Home className="w-4 h-4" />
                                WFH
                              </div>
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">
                              <div className="flex items-center justify-end gap-1">
                                <PartyPopper className="w-4 h-4" />
                                Event
                              </div>
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">
                              <div className="flex items-center justify-end gap-1">
                                <Briefcase className="w-4 h-4" />
                                Field
                              </div>
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Entries</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyHours.map((record, index) => {
                            const employee = employees.find(e => e.user_id === record.user_id);
                            return (
                              <tr key={`${record.date}_${record.user_id}_${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-3 px-4">
                                  <p className="font-medium text-foreground-DEFAULT">
                                    {new Date(record.date).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </td>
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="font-medium text-foreground-DEFAULT">{record.user_name}</p>
                                    <p className="text-xs text-foreground-muted">{record.user_email}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  {employee?.group ? (
                                    <GroupBadge group={employee.group} />
                                  ) : (
                                    <span className="text-xs text-foreground-muted">No group</span>
                                  )}
                                </td>
                                <td className="text-right py-3 px-4">
                                  <span className="font-bold text-foreground-DEFAULT">{record.total_hours}h</span>
                                </td>
                                <td className="text-right py-3 px-4">
                                  <span className="text-foreground-muted">{record.onsite_hours > 0 ? `${record.onsite_hours}h` : '-'}</span>
                                </td>
                                <td className="text-right py-3 px-4">
                                  <span className="text-foreground-muted">{record.wfh_hours > 0 ? `${record.wfh_hours}h` : '-'}</span>
                                </td>
                                <td className="text-right py-3 px-4">
                                  <span className="text-foreground-muted">{record.event_hours > 0 ? `${record.event_hours}h` : '-'}</span>
                                </td>
                                <td className="text-right py-3 px-4">
                                  <span className="text-foreground-muted">{record.field_hours > 0 ? `${record.field_hours}h` : '-'}</span>
                                </td>
                                <td className="text-right py-3 px-4 text-foreground-muted">
                                  {record.entries_count}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="breakdown">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-2">Daily Breakdown by Day of Week</h2>
                        <p className="text-sm text-foreground-muted">View attendance data filtered by specific days (e.g., all Mondays)</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleExport('excel', 'daily-breakdown')}
                          className="bg-accent hover:bg-accent/90 rounded-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Excel
                        </Button>
                        <Button
                          onClick={() => handleExport('pdf', 'daily-breakdown')}
                          className="bg-primary hover:bg-primary-hover rounded-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          PDF
                        </Button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="day-select" className="mb-2 block">Filter by Day of Week</Label>
                      <Select value={selectedDayOfWeek} onValueChange={handleDayOfWeekChange}>
                        <SelectTrigger id="day-select" className="w-full sm:w-64">
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Days</SelectItem>
                          <SelectItem value="monday">Monday</SelectItem>
                          <SelectItem value="tuesday">Tuesday</SelectItem>
                          <SelectItem value="wednesday">Wednesday</SelectItem>
                          <SelectItem value="thursday">Thursday</SelectItem>
                          <SelectItem value="friday">Friday</SelectItem>
                          <SelectItem value="saturday">Saturday</SelectItem>
                          <SelectItem value="sunday">Sunday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {dailyBreakdownLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <p className="text-foreground-muted">Loading breakdown data...</p>
                    </div>
                  ) : dailyBreakdown && dailyBreakdown.data ? (
                    <div>
                      {/* Summary Stats */}
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-foreground-muted mb-1">Total Hours</p>
                          <p className="text-2xl font-bold text-foreground-DEFAULT">{dailyBreakdown.summary?.total_hours || 0}h</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-foreground-muted mb-1">Total Entries</p>
                          <p className="text-2xl font-bold text-foreground-DEFAULT">{dailyBreakdown.summary?.total_entries || 0}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-foreground-muted mb-1">Unique Dates</p>
                          <p className="text-2xl font-bold text-foreground-DEFAULT">{dailyBreakdown.summary?.unique_dates || 0}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-foreground-muted mb-1">Unique Users</p>
                          <p className="text-2xl font-bold text-foreground-DEFAULT">{dailyBreakdown.summary?.unique_users || 0}</p>
                        </div>
                      </div>

                      {/* Breakdown Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Date</th>
                              <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Day</th>
                              <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Employee</th>
                              <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Total Hours</th>
                              <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Onsite</th>
                              <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">WFH</th>
                              <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Event</th>
                              <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Field</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dailyBreakdown.data.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="py-12 text-center">
                                  <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                                  <p className="text-foreground-muted">No data for selected day and period</p>
                                </td>
                              </tr>
                            ) : (
                              dailyBreakdown.data.map((row, index) => {
                                const date = new Date(row.date);
                                return (
                                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-3 px-4">
                                      <p className="text-foreground-DEFAULT">
                                        {date.toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-foreground-DEFAULT">
                                        {row.day_of_week}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <div>
                                        <p className="font-medium text-foreground-DEFAULT">{row.user_name}</p>
                                        <p className="text-xs text-foreground-muted">{row.user_email}</p>
                                      </div>
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                      <span className="font-semibold text-foreground-DEFAULT">{row.total_hours}h</span>
                                    </td>
                                    <td className="py-3 px-4 text-right text-foreground-muted">{row.onsite_hours}h</td>
                                    <td className="py-3 px-4 text-right text-foreground-muted">{row.wfh_hours}h</td>
                                    <td className="py-3 px-4 text-right text-foreground-muted">{row.event_hours}h</td>
                                    <td className="py-3 px-4 text-right text-foreground-muted">{row.field_hours}h</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                      <p className="text-foreground-muted">No breakdown data available</p>
                      <p className="text-sm text-foreground-muted mt-2">Select a day of week and date range to view data</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="logs">
                  <div className="mb-6">
                    <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-2">Time Entry Logs</h2>
                    <p className="text-sm text-foreground-muted">Detailed time entry records per employee</p>
                  </div>

                  {history.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                      <p className="text-foreground-muted">No time entries for selected period</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Employee</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Date</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Clock In</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Clock Out</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground-DEFAULT">Total Hours</th>
                            <th className="text-left py-3 px-4 font-semibold text-foreground-DEFAULT">Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history
                            .filter(entry => {
                              // Filter by group if selected
                              if (selectedGroup && selectedGroup !== 'all') {
                                const employee = employees.find(e => e.user_id === entry.user_id);
                                return employee?.group?.group_id === selectedGroup;
                              }
                              return true;
                            })
                            .sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in))
                            .map((entry) => {
                              const employee = employees.find(e => e.user_id === entry.user_id);
                              const clockInDate = new Date(entry.clock_in);
                              const clockOutDate = entry.clock_out ? new Date(entry.clock_out) : null;
                              const totalHours = entry.duration ? (entry.duration / 60).toFixed(2) : '-';

                              const getLocationIcon = (location) => {
                                switch (location) {
                                  case 'onsite':
                                    return <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />Onsite</span>;
                                  case 'wfh':
                                    return <span className="inline-flex items-center gap-1"><Home className="w-4 h-4" />WFH</span>;
                                  case 'event':
                                    return <span className="inline-flex items-center gap-1"><PartyPopper className="w-4 h-4" />Event</span>;
                                  case 'field':
                                    return <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" />Field</span>;
                                  default:
                                    return location || '-';
                                }
                              };

                              return (
                                <tr key={entry.entry_id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-3 px-4">
                                    <div>
                                      <p className="font-medium text-foreground-DEFAULT">{entry.user_name || employee?.name || 'Unknown'}</p>
                                      <p className="text-xs text-foreground-muted">{entry.user_email || employee?.email || ''}</p>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <p className="text-foreground-DEFAULT">
                                      {clockInDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </p>
                                    <p className="text-xs text-foreground-muted">
                                      {clockInDate.toLocaleDateString('en-US', { weekday: 'short' })}
                                    </p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <p className="text-foreground-DEFAULT">
                                      {clockInDate.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <p className="text-foreground-DEFAULT">
                                      {clockOutDate ? (
                                        clockOutDate.toLocaleTimeString('en-US', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          hour12: true
                                        })
                                      ) : (
                                        <span className="text-accent font-medium">In Progress</span>
                                      )}
                                    </p>
                                  </td>
                                  <td className="text-right py-3 px-4">
                                    <span className="font-semibold text-foreground-DEFAULT">
                                      {totalHours !== '-' ? `${totalHours}h` : '-'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-sm text-foreground-DEFAULT">
                                      {getLocationIcon(entry.location)}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}

          {user.role !== 'admin' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-primary/10 rounded-xl p-3">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground-DEFAULT">Total Hours</h3>
                </div>
                <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                  {stats.totalHours}h
                </p>
                <p className="text-sm text-foreground-muted mt-2">
                  {stats.totalDays} working days
                </p>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-accent/10 rounded-xl p-3">
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold text-foreground-DEFAULT">Avg Hours/Day</h3>
                </div>
                <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                  {stats.avgHoursPerDay}h
                </p>
                <p className="text-sm text-foreground-muted mt-2">
                  Per working day
                </p>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-green-100 rounded-xl p-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-foreground-DEFAULT">Approved Leaves</h3>
                </div>
                <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                  {leaveStats[0].value}
                </p>
                <p className="text-sm text-foreground-muted mt-2">
                  In selected period
                </p>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-orange-100 rounded-xl p-3">
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-foreground-DEFAULT">Pending Leaves</h3>
                </div>
                <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                  {leaveStats[1].value}
                </p>
                <p className="text-sm text-foreground-muted mt-2">
                  Awaiting approval
                </p>
              </div>
            </div>
          )}

          {user.role !== 'admin' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="chart-container">
                  <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-6">Daily Hours Worked</h2>
                  {chartData.length === 0 ? (
                    <p className="text-foreground-muted text-center py-12">No data for selected period</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
                        <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="hours" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-6">Leave Status</h2>
                  {leaveStats.every(s => s.value === 0) ? (
                    <p className="text-foreground-muted text-center py-12">No leave requests in selected period</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={leaveStats.filter(s => s.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {leaveStats.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {employeeReport && (
                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-6">Recent Activity Summary</h2>
                  <div className="space-y-4">
                    {employeeReport.time_entries?.slice(0, 10).map((entry) => (
                      <div
                        key={entry.entry_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-foreground-DEFAULT">
                            {new Date(entry.clock_in).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-sm text-foreground-muted">
                            {new Date(entry.clock_in).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            {entry.clock_out && (
                              <>
                                {' - '}
                                {new Date(entry.clock_out).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-foreground-DEFAULT">
                            {entry.duration ? `${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m` : 'In Progress'}
                          </p>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              entry.status === 'active'
                                ? 'bg-accent/10 text-accent'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {entry.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Reports;
